import { useState, useEffect, type FormEvent, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../api/client.js'
import { useAuthStore, type User } from '../store/authStore.js'
import voz360Logo from '../assets/voz360.png'
import LoginMfaDialog from '../components/auth/LoginMfaDialog.js'

interface PlatformLoginApiResponse {
  requiresMfa?: boolean
  setupRequired?: boolean
  secret?: string
  otpauthUrl?: string
  preAuthToken?: string
  platformAdmin?: {
    id: string
    email: string
    tenantId?: string
    tenantSlug?: string
    roles?: string[]
    permissions?: string[]
    isSuperAdmin?: boolean
  }
  csrfToken?: string
}

export default function PlatformLoginPage(): JSX.Element {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeSlide, setActiveSlide] = useState(0)
  const [mfaOpen, setMfaOpen] = useState(false)
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null)
  const [mfaSetup, setMfaSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  const slides = [
    {
      title: 'Platform Management',
      description: 'Streamline operations with centralized control and powerful automation tools.',
      features: [
        {
          label: 'Secure',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.75h-.152c-3.196 0-6.1-1.248-8.25-3.286z" />
          ),
        },
        {
          label: 'Analytics',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          ),
        },
        {
          label: 'Intelligent',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5c-1.036 0-1.876.84-1.876 1.875 0 .285.065.555.181.797l-4.32 2.508a1.87 1.87 0 00-1.11-.36c-1.036 0-1.875.84-1.875 1.875s.84 1.875 1.875 1.875c.41 0 .789-.132 1.098-.354l4.34 2.507a1.9 1.9 0 00-.166.762c0 1.035.84 1.875 1.875 1.875s1.875-.84 1.875-1.875-.84-1.875-1.875-1.875a1.87 1.87 0 00-1.11.36l-4.32-2.508a1.9 1.9 0 00.15-.75 1.9 1.9 0 00-.15-.75l4.32-2.508a1.87 1.87 0 001.11.36c1.036 0 1.876-.84 1.876-1.875S14.536 4.5 13.5 4.5z" />
          ),
        },
      ],
    },
    {
      title: 'Multi-Tenant Architecture',
      description: 'Scale effortlessly with isolated environments and dedicated resources per tenant.',
      features: [
        {
          label: 'Scalable',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
          ),
        },
        {
          label: 'Isolated',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          ),
        },
        {
          label: 'Flexible',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
          ),
        },
      ],
    },
    {
      title: 'Real-Time Monitoring',
      description: 'Track performance metrics and system health with live dashboards and alerts.',
      features: [
        {
          label: 'Live',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          ),
        },
        {
          label: 'Alerts',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          ),
        },
        {
          label: 'Insights',
          icon: (
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          ),
        },
      ],
    },
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [])

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const resp = await api.post<PlatformLoginApiResponse>('/auth/platform/login', { username, password })
      if (resp.requiresMfa) {
        if (!resp.preAuthToken) {
          setError('Missing MFA challenge token')
          return
        }
        setPreAuthToken(resp.preAuthToken)
        if (resp.setupRequired && resp.secret && resp.otpauthUrl) {
          setMfaSetup({ secret: resp.secret, otpauthUrl: resp.otpauthUrl })
        } else {
          setMfaSetup(null)
        }
        setMfaOpen(true)
        return
      }
      if (resp.platformAdmin && resp.csrfToken) {
        const isSuperAdmin = resp.platformAdmin?.isSuperAdmin ?? false
        const user: User = {
          id: resp.platformAdmin.id,
          sub: resp.platformAdmin.id,
          email: resp.platformAdmin.email,
          tenantId: resp.platformAdmin.tenantId ?? 'platform',
          tenantSlug: resp.platformAdmin.tenantSlug ?? 'platform',
          roles: resp.platformAdmin.roles ?? [],
          permissions: resp.platformAdmin.permissions ?? [],
          isSuperAdmin,
          portal: 'admin',
        }
        
        setSession({ user, csrfToken: resp.csrfToken })

        // Small delay to ensure state is persisted
        await new Promise(resolve => setTimeout(resolve, 100))
        
        navigate(isSuperAdmin ? '/platform' : '/admin/tenant/org-details')
        return
      }
      setError('Unexpected response from server')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen w-full">
      {mfaOpen && preAuthToken && (
        <LoginMfaDialog
          preAuthToken={preAuthToken}
          setup={mfaSetup}
          onClose={() => setMfaOpen(false)}
          mode="platform"
          onSuccess={(user) =>
            navigate(user.isSuperAdmin ? '/platform' : '/admin/tenant/org-details')
          }
        />
      )}
      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -20px) scale(1.05); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-15px, 15px) scale(1.08); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(10px, 10px) scale(0.95); }
        }
        @keyframes waveGlow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes btnGlow {
          0%, 100% { box-shadow: 0 8px 24px rgba(37,99,235,0.35); }
          50% { box-shadow: 0 8px 32px rgba(37,99,235,0.5); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* LEFT PANEL — Brand */}
      <div className="hidden lg:flex lg:w-[40%] flex-col justify-between p-12 relative overflow-hidden" style={{
        background: 'linear-gradient(160deg, #081235 0%, #0D1A4D 55%, #151B60 100%)',
      }}>
        {/* Animated brand blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] right-[-10%] h-[500px] w-[500px] rounded-full" style={{
            background: 'radial-gradient(circle, rgba(59,130,246,0.30) 0%, rgba(59,130,246,0.08) 40%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'float1 12s ease-in-out infinite',
          }} />
          <div className="absolute bottom-[-10%] left-[-15%] h-[400px] w-[400px] rounded-full" style={{
            background: 'radial-gradient(circle, rgba(255,159,26,0.18) 0%, rgba(255,159,26,0.05) 40%, transparent 70%)',
            filter: 'blur(50px)',
            animation: 'float2 15s ease-in-out infinite',
          }} />
          <div className="absolute top-[40%] left-[20%] h-[300px] w-[300px] rounded-full" style={{
            background: 'radial-gradient(circle, rgba(79,70,229,0.18) 0%, transparent 60%)',
            filter: 'blur(40px)',
            animation: 'float3 10s ease-in-out infinite reverse',
          }} />
          {/* Top right corner bubble with different color */}
          <div className="absolute top-[5%] right-[5%] h-[180px] w-[180px] rounded-full" style={{
            background: 'radial-gradient(circle, rgba(236,72,153,0.25) 0%, rgba(236,72,153,0.08) 50%, transparent 70%)',
            filter: 'blur(35px)',
            animation: 'float1 8s ease-in-out infinite',
          }} />
        </div>

        {/* Tiny dotted particles */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        {/* Top: Logo */}
        <div className="relative z-10">
          <img src={voz360Logo} alt="Voz360" className="h-10 w-auto" />
        </div>

        {/* Center: Tagline */}
        <div className="relative z-10 max-w-sm">
          <h2 className="font-extrabold text-white leading-[1.05] tracking-tight transition-all duration-500" style={{ fontSize: '52px' }}>
            <span className="block">{slides[activeSlide]?.title.split(' ')[0]}</span>
            <span className="block" style={{ color: '#FF9F1A' }}>{slides[activeSlide]?.title.split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed transition-all duration-500" style={{ color: '#B8C0D9' }}>
            {slides[activeSlide]?.description}
          </p>

          {/* Feature icons — glassmorphism circular buttons */}
          <div className="mt-9 flex items-center gap-8">
            {slides[activeSlide]?.features.map((feature, index) => (
              <div key={feature.label} className="flex flex-col items-center gap-2.5" style={{
                animation: `slideIn 0.6s ease-out ${index * 0.1}s both`,
              }}>
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 hover:scale-110 hover:shadow-lg"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    borderColor: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#7C9BFF" strokeWidth={1.5}>
                    {feature.icon}
                  </svg>
                </div>
                <span className="text-[12px] font-medium transition-colors duration-300 hover:text-white" style={{ color: '#B8C0D9' }}>
                  {feature.label}
                </span>
              </div>
            ))}
          </div>

          {/* Dots indicator */}
          <div className="mt-10 flex items-center gap-2">
            {slides.map((_: unknown, index: number) => (
              <div
                key={index}
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  width: index === activeSlide ? '32px' : '8px',
                  background: index === activeSlide ? '#FF9F1A' : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>
        </div>

        {/* Static wave lines at bottom */}
        <div className="absolute bottom-0 left-0 w-full pointer-events-none overflow-hidden" style={{ height: '280px' }}>
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1200 280"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: '280px' }}
          >
            <path d="M0 200C150 160 300 240 450 180C600 120 750 210 900 150C1050 90 1200 100 1200 100V280H0V200Z"
              stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" fill="none" />
            <path d="M0 220C150 190 300 260 450 210C600 160 750 230 900 180C1050 130 1200 140 1200 140V280H0V220Z"
              stroke="#4F46E5" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" fill="none" />
            <path d="M0 240C150 220 300 270 450 230C600 190 750 250 900 210C1050 170 1200 180 1200 180V280H0V240Z"
              stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" fill="none" />
          </svg>
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1200 280"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: '280px' }}
          >
            <path d="M0 180C120 140 280 220 400 160C520 100 680 190 800 130C920 70 1080 80 1200 80V280H0V180Z"
              stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" fill="none" />
            <path d="M0 210C120 180 280 250 400 200C520 150 680 220 800 170C920 120 1080 130 1200 130V280H0V210Z"
              stroke="#818CF8" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" fill="none" />
          </svg>
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1200 280"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: '280px' }}
          >
            <path d="M0 230C180 190 360 270 540 210C720 150 900 240 1080 180C1140 160 1200 170 1200 170V280H0V230Z"
              stroke="#FF9F1A" strokeWidth="1.5" strokeLinecap="round" opacity="0.25" fill="none" />
            <path d="M0 250C180 220 360 280 540 240C720 200 900 260 1080 220C1140 200 1200 210 1200 210V280H0V250Z"
              stroke="#FBBF24" strokeWidth="1.5" strokeLinecap="round" opacity="0.2" fill="none" />
          </svg>
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1200 280"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: '280px' }}
          >
            <path d="M0 200C100 170 200 230 300 190C400 150 500 200 600 160C700 120 800 130 900 130C1000 130 1100 140 1200 140V280H0V200Z"
              stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" fill="none" />
            <path d="M0 225C100 200 200 255 300 220C400 185 500 230 600 195C700 160 800 170 900 170C1000 170 1100 180 1200 180V280H0V225Z"
              stroke="#C4B5FD" strokeWidth="1.5" strokeLinecap="round" opacity="0.25" fill="none" />
          </svg>
          {/* Additional horizontal lines */}
          <svg
            className="absolute bottom-0 left-0 w-full"
            viewBox="0 0 1200 280"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: '280px' }}
          >
            <line x1="0" y1="260" x2="1200" y2="260" stroke="#3B82F6" strokeWidth="1" opacity="0.3" />
            <line x1="0" y1="265" x2="1200" y2="265" stroke="#4F46E5" strokeWidth="1" opacity="0.25" />
            <line x1="0" y1="270" x2="1200" y2="270" stroke="#6366F1" strokeWidth="1" opacity="0.2" />
          </svg>
        </div>

        {/* Bottom */}
        <div className="relative z-10 text-[13px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
          &copy; {new Date().getFullYear()} VOZ360. All rights reserved.
        </div>
      </div>

      {/* RIGHT PANEL — Form */}
      <div className="flex flex-1 items-center justify-center p-6 lg:p-12 relative overflow-hidden" style={{ background: '#F8FAFC' }}>
        {/* Soft ambient circles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] h-[400px] w-[400px] rounded-full opacity-[0.05]" style={{
            background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)',
            filter: 'blur(50px)',
          }} />
          <div className="absolute bottom-[-10%] left-[-5%] h-[350px] w-[350px] rounded-full opacity-[0.05]" style={{
            background: 'radial-gradient(circle, #FF9F1A 0%, transparent 70%)',
            filter: 'blur(50px)',
          }} />
          {/* Half bubble in top right */}
          <div className="absolute top-0 right-0 h-[200px] w-[200px] rounded-bl-full opacity-[0.08]" style={{
            background: 'radial-gradient(circle at top right, #2563EB 0%, transparent 70%)',
            filter: 'blur(40px)',
          }} />
          <div
            className="absolute bottom-0 right-0 h-[220px] w-[260px] opacity-[0.35]"
            style={{
              backgroundImage: 'radial-gradient(circle, #c7cde3 1.5px, transparent 1.5px)',
              backgroundSize: '18px 18px',
              maskImage: 'radial-gradient(circle at bottom right, black 0%, transparent 75%)',
              WebkitMaskImage: 'radial-gradient(circle at bottom right, black 0%, transparent 75%)',
            }}
          />
        </div>

        <div className="relative z-10 w-full" style={{ maxWidth: '520px' }}>
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={voz360Logo} alt="Voz360" className="h-9 w-auto" />
          </div>

          {/* Card */}
          <div
            className="bg-white relative"
            style={{
              borderRadius: '28px',
              padding: '48px',
              boxShadow: '0 25px 60px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.05)',
              animation: 'slideIn 0.5s ease-out',
            }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden transition-transform duration-300 hover:scale-105"
                style={{ borderRadius: '14px', background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}
              >
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none">
                  <path d="M12 21c-4.5-1-8-5-8-9.5A8 8 0 0112 3" stroke="white" strokeWidth={1.75} strokeLinecap="round" />
                  <path d="M15.5 6.2a5.5 5.5 0 010 10.7" stroke="white" strokeWidth={1.75} strokeLinecap="round" opacity="0.9" />
                  <path d="M14.3 9a2.6 2.6 0 010 5.2" stroke="white" strokeWidth={1.75} strokeLinecap="round" />
                  <circle cx="11.3" cy="10.6" r="1.6" fill="white" />
                  <path d="M8.3 17.3c0-1.66 1.34-2.6 3-2.6s3 .94 3 2.6" stroke="white" strokeWidth={1.75} strokeLinecap="round" fill="none" /></svg>
              </div>
              <div>
                <h1 className="font-bold tracking-tight" style={{ fontSize: '32px', color: '#111827' }}>Welcome back</h1>
                <p className="mt-1 text-[15px]" style={{ color: '#64748B' }}>Sign in to your platform account</p>
              </div>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                <p className="text-[13px] text-red-600 font-medium" role="alert">{error}</p>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="mb-2 block text-[13px] font-medium" style={{ color: '#374151' }}>Username</label>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px]" style={{ color: '#94A3B8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoComplete="username"
                    className="w-full pl-11 pr-4 text-[14px] placeholder-gray-400 outline-none transition-all duration-200"
                    style={{
                      height: '56px',
                      borderRadius: '14px',
                      border: '1px solid #E5E7EB',
                      color: '#111827',
                      background: '#FAFAFA',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; e.currentTarget.style.background = '#FFFFFF' }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = '#FAFAFA' }}
                    placeholder="Enter your username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-[13px] font-medium" style={{ color: '#374151' }}>Password</label>
                <div className="relative">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px]" style={{ color: '#94A3B8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full pl-11 pr-11 text-[14px] placeholder-gray-400 outline-none transition-all duration-200"
                    style={{
                      height: '56px',
                      borderRadius: '14px',
                      border: '1px solid #E5E7EB',
                      color: '#111827',
                      background: '#FAFAFA',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; e.currentTarget.style.background = '#FFFFFF' }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = '#FAFAFA' }}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 h-full pr-4 flex items-center transition-colors"
                    style={{ color: '#94A3B8' }}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label htmlFor="rememberMe" className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded accent-[#2563EB]"
                    style={{ borderColor: '#E5E7EB' }}
                  />
                  <span className="text-[13px]" style={{ color: '#374151' }}>Remember me</span>
                </label>
                <a href="/forgot-password" className="text-[13px] font-medium transition-colors" style={{ color: '#2563EB' }}>
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative w-full overflow-hidden text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-lg"
                style={{
                  height: '54px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)',
                  boxShadow: '0 8px 24px rgba(37,99,235,0.35)',
                }}
              >
                {/* Shine effect on hover */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                }} />
                <span className="relative z-10 flex items-center justify-center gap-2 text-[15px]">
                  {loading ? (
                    <>
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Authenticating...
                    </>
                  ) : (
                    <>
                      Sign In
                      <svg className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                      </svg>
                    </>
                  )}
                </span>
              </button>
            </form>

            <div className="mt-6 flex items-center gap-3">
              <div className="h-px flex-1" style={{ background: '#E5E7EB' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#94A3B8' }}>OR</span>
              <div className="h-px flex-1" style={{ background: '#E5E7EB' }} />
            </div>

            <div className="mt-6 text-center">
              <a
                href="/login"
                className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 transition-colors"
                style={{ color: '#2563EB', borderRadius: '10px' }}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18" />
                </svg>
                Back to Tenant Login
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
